import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Spinner from "../../../components/Spinner";
import Modal from "../../../components/Modal";
import ModalContainer from "./ModalContainer.js";
import { db } from "../../../FirebaseConfig.ts";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { formatCurrencyTable } from "../../../utils/formatCurrencyTable.js";
import { useSnapshotWhere } from "../../../hooks/useSnapshotWhere.js";

function SearchSimulator({ user }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const { data: dataUser, isFetching: fetchingUser } = useSnapshotWhere(
    "users",
    {
      filter: {
        firstCondition: "id",
        secondCondition: "==",
        thirdCondition: user.uid,
      },
    }
  );

  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [activeSearch, setActiveSearch] = useState(false);
  const [number, setNumber] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentRequest, setCurrentRequest] = useState({});

  const categoriesCollection = collection(db, "simulator");
  const companyId = dataUser[0]?.majorCompanyId;

  useEffect(() => {
    setIsLoaded(true);
    if (companyId) {
      fetchInitial();
    }
  }, [companyId]);

  const fetchCategories = async () => {
    try {
      setIsFetching(true);
      const q = query(
        categoriesCollection,
        where("majorCompanyId", "==", companyId),
        where("identity", "==", number),
        where("status", "==", "pendiente")
      );
      const data = await getDocs(q);
      setData(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      console.log(error);
      setIsFetching(false);
    } finally {
      setIsFetching(false);
    }
  };

  const fetchInitial = async () => {
    try {
      setIsFetching(true);
      const q = query(
        categoriesCollection,
        where("majorCompanyId", "==", companyId),
        where("status", "==", "pendiente")
      );
      const data = await getDocs(q);
      setData(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      console.log(error);
      setIsFetching(false);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSearch = () => {
    if (number.trim()) {
      fetchCategories();
      setActiveSearch(true);
    }
  };

  const handleClearSearch = () => {
    fetchInitial();
    setActiveSearch(false);
    setNumber("");
  };

  // 🔥 Nuevo: función para eliminar solicitud
  const handleDeleteRequest = async (id) => {
    const confirmDelete = window.confirm(
      "¿Estás seguro de eliminar esta solicitud?"
    );
    if (!confirmDelete) return;

    try {
      setDeletingId(id);
      await deleteDoc(doc(db, "simulator", id));
      setData((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error eliminando la solicitud:", error);
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendiente: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        dot: "bg-yellow-400",
        label: "Pendiente",
      },
      aprobado: {
        bg: "bg-green-100",
        text: "text-green-800",
        dot: "bg-green-400",
        label: "Aprobado",
      },
      rechazado: {
        bg: "bg-red-100",
        text: "text-red-800",
        dot: "bg-red-400",
        label: "Rechazado",
      },
    };

    const config = statusConfig[status] || statusConfig.pendiente;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></div>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div
        className={`transition-all duration-700 ease-out ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="space-y-1 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center justify-center gap-3">
            <div className="p-2 bg-[#49b0a8]/10 rounded-xl">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M11 6C13.7614 6 16 8.23858 16 11M16.6588 16.6549L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            Buscar Simulaciones
          </h1>
          <p className="text-gray-600">
            Encuentra y consulta todas las simulaciones de crédito realizadas
          </p>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex gap-3 justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Búsqueda de Simulaciones
            </h2>
            <div className="flex gap-3 items-center -mt-3">
              <p className="text-xs text-gray-600">
                Total Simulaciones Pendientes
              </p>
              <p className="text-lg font-bold text-gray-900">{data.length}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="search"
                className="block w-full pl-12 pr-32 py-4 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Buscar por número de cédula..."
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                disabled={fetchingUser}
              />
              <button
                onClick={handleSearch}
                disabled={fetchingUser || !number.trim()}
                className={`absolute right-2 top-2 bottom-2 bg-[#49b0a8] hover:bg-[#3a8a84] text-white px-6 rounded-lg font-medium transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {fetchingUser ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  "Buscar"
                )}
              </button>
            </div>

            {/* Search Status */}
            {activeSearch && (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm text-blue-800">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>
                    Búsqueda activa para: <strong>{number}</strong>
                  </span>
                </div>
                <button
                  onClick={handleClearSearch}
                  className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 18L18 6M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Limpiar filtros
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal showModal={showModal} setShowModal={setShowModal}>
        <ModalContainer
          currentRequest={currentRequest}
          setCurrentRequest={setCurrentRequest}
        />
      </Modal>

      {/* Results Section */}
      <div
        className={`transition-all duration-700 ease-out delay-300 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        {isFetching ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <Spinner />
                <p className="text-gray-600">Buscando simulaciones...</p>
              </div>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="text-center py-12 space-y-4">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-gray-400"
                >
                  <path
                    d="M11 6C13.7614 6 16 8.23858 16 11M16.6588 16.6549L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  No se encontraron simulaciones
                </h3>
                <p className="text-gray-600">
                  {activeSearch
                    ? `No existen simulaciones con la cédula "${number}"`
                    : "Aún no hay simulaciones registradas"}
                </p>
              </div>
              {activeSearch && (
                <button
                  onClick={handleClearSearch}
                  className="mt-4 bg-[#49b0a8] hover:bg-[#3a8a84] text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                >
                  Ver todas las simulaciones
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cédula
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.map((el, index) => (
                    <tr
                      key={el.id}
                      className={`
                        transition-all duration-200 hover:bg-gray-50 group
                        ${
                          isLoaded
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-4"
                        }
                      `}
                      style={{
                        animationDelay: `${index * 100}ms`,
                        animation: isLoaded
                          ? "fadeInUp 0.6s ease-out forwards"
                          : "none",
                      }}
                      onMouseEnter={() => setHoveredRow(index)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600 group-hover:bg-[#49b0a8] group-hover:text-white transition-colors duration-200">
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{`${el.name} ${el.lastName}`}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {el.identity}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          ${formatCurrencyTable(Number(el.ammount))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(el.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              setShowModal(true);
                              setCurrentRequest(el);
                            }}
                            className="text-[#49b0a8] hover:text-[#3a8a84] font-medium text-sm transition-colors duration-200"
                          >
                            Ver Solicitud
                          </button>
                          <Link to={`/plataforma/simulador/${el.id}`}>
                            <button className="text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors duration-200">
                              Ver Crédito
                            </button>
                          </Link>

                          {/* 🔥 Nuevo botón eliminar */}
                          <button
                            onClick={() => handleDeleteRequest(el.id)}
                            disabled={deletingId === el.id}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            {deletingId === el.id
                              ? "Eliminando..."
                              : "Eliminar"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-4 p-4">
              {data?.map((el, index) => (
                <div
                  key={el.id}
                  className={`
                    bg-white border border-gray-200 rounded-xl p-4 space-y-4 hover:shadow-md transition-all duration-300
                    ${
                      isLoaded
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-4"
                    }
                  `}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: isLoaded
                      ? "fadeInUp 0.6s ease-out forwards"
                      : "none",
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h3 className="font-medium text-gray-900">{`${el.name} ${el.lastName}`}</h3>
                      <p className="text-sm text-gray-500">ID: {el.identity}</p>
                    </div>
                    <div className="text-right space-y-2">
                      <div className="text-lg font-semibold text-gray-900">
                        ${formatCurrencyTable(Number(el.ammount))}
                      </div>
                      {getStatusBadge(el.status)}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setShowModal(true);
                        setCurrentRequest(el);
                      }}
                      className="flex-1 bg-[#49b0a8] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#3a8a84] transition-colors duration-200"
                    >
                      Ver Solicitud
                    </button>
                    <Link to={`/plataforma/simulador/${el.id}`}>
                      <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors duration-200">
                        Ver Crédito
                      </button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default SearchSimulator;
